import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Download, FileText, MessageSquare, RefreshCw, UserPlus, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Flex, Badge, Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText, EmptyHint, SkeletonRow
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

export default function ConversationLogs() {
  const { t } = useTranslation('callcenter');
  const { tenantId } = useParams();
  const [conversations, setConversations]       = useState([]);
  const [selected, setSelected]                 = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [loadingMore, setLoadingMore]           = useState(false);
  const [exporting, setExporting]               = useState('');
  const [search, setSearch]                     = useState('');
  const [filterAgent, setFilterAgent]           = useState('');
  // Filtro por intervalo de data/hora (datetime-local → ISO). Vazio = sem limite
  // (o backend ainda aplica o teto da retenção do tenant).
  const [filterFrom, setFilterFrom]             = useState('');
  const [filterTo, setFilterTo]                 = useState('');
  const [nextKey, setNextKey]                   = useState(null);
  const [agents, setAgents]                     = useState([]); // { instanceId, name }
  const [terminating, setTerminating]           = useState(false);
  const [queuing, setQueuing]                   = useState(false);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptExpired, setTranscriptExpired] = useState(false);

  const buildParams = useCallback((lk) => {
    const p = new URLSearchParams({ limit: '50' });
    if (filterAgent) p.set('agentId', filterAgent);
    if (filterFrom) p.set('from', new Date(filterFrom).toISOString());
    if (filterTo) p.set('to', new Date(filterTo).toISOString());
    if (lk) p.set('lastKey', lk);
    return p.toString();
  }, [filterAgent, filterFrom, filterTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setNextKey(null);
    try {
      const [convRes, agentRes] = await Promise.all([
        api.get(`/conversations/${tenantId}/logs?${buildParams()}`),
        api.get(`/marketplace/tenants/${tenantId}/agents`),
      ]);
      setConversations(convRes.data.conversations || []);
      setNextKey(convRes.data.nextKey || null);
      setAgents(agentRes.data.agents || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId, buildParams]);

  const loadMore = async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/conversations/${tenantId}/logs?${buildParams(nextKey)}`);
      const convs = res.data.conversations || [];
      setConversations(prev => [...prev, ...convs]);
      setNextKey(res.data.nextKey || null);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  useEffect(() => { load(); }, [load]);

  // agentOptions: lista completa de agentes instalados
  const agentOptions = agents;

  // Helper: nome legível do agente pelo instanceId
  const agentName = (id) => agents.find(a => a.instanceId === id)?.name || id;

  // Abre uma conversa e carrega a transcrição sob demanda (endpoint dedicado com
  // trilha de auditoria LGPD no backend). Trata o caso de transcrição expirada.
  const openConversation = useCallback(async (conv) => {
    setSelected({ ...conv, messages: conv.messages || [] });
    setTranscriptExpired(false);
    if (!conv.transcriptS3Key) {
      // Sem ponteiro: nada a carregar (sessão antiga à feature ou sem transcrição)
      setTranscriptExpired(true);
      return;
    }
    setTranscriptLoading(true);
    try {
      const p = new URLSearchParams({
        transcript: '1',
        sessionKey: conv.id,
        startedAt: conv.startTime || '',
      });
      const res = await api.get(`/conversations/${tenantId}/logs?${p.toString()}`);
      if (res.data.expired || !res.data.url) {
        setTranscriptExpired(true);
        return;
      }
      // A URL presignada aponta para o JSON da transcrição no S3.
      const transcript = await fetch(res.data.url).then(r => r.json());
      const messages = (transcript.messages || []).map(m => ({
        role: m.role, content: m.content, timestamp: m.timestamp || conv.startTime,
      }));
      setSelected(prev => (prev && prev.id === conv.id ? { ...prev, messages } : prev));
    } catch (e) {
      console.error('load transcript error:', e);
      setTranscriptExpired(true);
    } finally {
      setTranscriptLoading(false);
    }
  }, [tenantId]);

  // Filtragem client-side apenas para busca por texto (agente e período já filtrados no backend)
  const filtered = conversations.filter(conv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (conv.userName || '').toLowerCase().includes(q) ||
      (conv.userId || '').toLowerCase().includes(q)
    );
  });

  const handleTerminate = async (conv) => {
    if (!window.confirm(t('terminateConfirm'))) return;
    setTerminating(true);
    try {
      await api.post(`/conversations/${tenantId}/resolve`, { sessionId: conv.id });
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, status: 'completed' } : c));
      setSelected(prev => prev?.id === conv.id ? { ...prev, status: 'completed' } : prev);
    } catch (e) { console.error(e); }
    finally { setTerminating(false); }
  };

  const handleSendToQueue = async (conv) => {
    if (!window.confirm(t('sendToQueueConfirm'))) return;
    setQueuing(true);
    try {
      await api.post(`/conversations/${tenantId}/transfer`, { sessionId: conv.id, targetAgentType: 'queue' });
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, status: 'queued' } : c));
      setSelected(prev => prev?.id === conv.id ? { ...prev, status: 'queued' } : prev);
    } catch (e) { console.error(e); }
    finally { setQueuing(false); }
  };

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/conversations/${tenantId}/logs?export=${fmt}&limit=200`);
      const { url } = res.data;
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversas-${tenantId}-${new Date().toISOString().slice(0,10)}.${fmt === 'csv' ? 'csv' : 'html'}`;
      a.click();
    } catch (e) { console.error(e); }
    finally { setExporting(''); }
  };

  // Normaliza content que pode ser string, array de objetos (Bedrock format), ou objeto
  const formatContent = (content) => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter(c => c.type === 'text' || c.text)
        .map(c => c.text || '')
        .join('\n') || '[conteúdo não textual]';
    }
    if (content && typeof content === 'object') {
      return content.text || JSON.stringify(content);
    }
    return '';
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    user.role === 'sallus_admin'
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('home'), path: `/dashboard/${tenantId}` },
    { label: t('breadcrumbDashboard'), path: user.role === 'sallus_admin' ? `/dashboard/${tenantId}` : undefined },
    { label: t('logsTitle') }
  ].filter(i => i.label);

  return (
    <>
      <AppHeader title={t('logsTitle')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>{t('logsTitle')}</PageTitle>
            <PageSubtitle>{t('logsSubtitleFull')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            <ExportBtn onClick={() => handleExport('csv')} disabled={!!exporting}>
              {exporting === 'csv' ? <Spinner size="13px" /> : <Download size={13} />}
              CSV
            </ExportBtn>
            <ExportBtn onClick={() => handleExport('pdf')} disabled={!!exporting}>
              {exporting === 'pdf' ? <Spinner size="13px" /> : <FileText size={13} />}
              HTML/PDF
            </ExportBtn>
            <ExportBtn $secondary onClick={load} disabled={loading}>
              <RefreshCw size={13} />
            </ExportBtn>
          </Flex>
        </PageHeader>

        {loading ? (
          <SkeletonList>
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonRow key={i} avatar lines={2} />
            ))}
          </SkeletonList>
        ) : (
          <>
          <FilterBar>
            <FilterInput
              placeholder={t('searchContactPlaceholder', { defaultValue: 'Buscar por contato ou número...' })}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <FilterSelect value={filterAgent} onChange={e => setFilterAgent(e.target.value)}>
              <option value="">{t('allAgents', { defaultValue: 'Todos os agentes' })}</option>
              {agentOptions.map(a => (
                <option key={a.instanceId} value={a.instanceId}>
                  {a.icon ? `${a.icon} ` : ''}{a.name}
                </option>
              ))}
            </FilterSelect>
            <FilterDateGroup>
              <FilterDateLabel>{t('filterFrom', { defaultValue: 'De' })}</FilterDateLabel>
              <FilterDate
                type="datetime-local"
                value={filterFrom}
                max={filterTo || undefined}
                onChange={e => setFilterFrom(e.target.value)}
              />
              <FilterDateLabel>{t('filterTo', { defaultValue: 'Até' })}</FilterDateLabel>
              <FilterDate
                type="datetime-local"
                value={filterTo}
                min={filterFrom || undefined}
                onChange={e => setFilterTo(e.target.value)}
              />
              {(filterFrom || filterTo) && (
                <ClearDatesBtn onClick={() => { setFilterFrom(''); setFilterTo(''); }} title={t('clearDates', { defaultValue: 'Limpar' })}>
                  <XCircle size={13} />
                </ClearDatesBtn>
              )}
            </FilterDateGroup>
            <FilterCount>{t('conversationCount', { count: filtered.length })}</FilterCount>
          </FilterBar>
          <Grid>
            {/* Lista */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <ListHeader>
                <MessageSquare size={14} />
                {t('conversationCount', { count: filtered.length })}
              </ListHeader>
              {filtered.length === 0 ? (
                <EmptyState style={{ padding: theme.spacing[8] }}>
                  <EmptyIcon>💬</EmptyIcon>
                  <EmptyText>{t('noConversations')}</EmptyText>
                  <EmptyHint style={{ marginTop: theme.spacing[3], textAlign: 'center' }}>
                    {t('noConversationsHint', { defaultValue: 'As conversas do seu assistente aparecem aqui. Envie uma mensagem para começar.' })}
                  </EmptyHint>
                </EmptyState>
              ) : (
                <ListScroll>
                  {filtered.map(conv => (
                    <ConvItem
                      key={conv.id}
                      $active={selected?.id === conv.id}
                      onClick={() => openConversation(conv)}
                    >
                      <Flex justify="space-between" style={{ marginBottom: 4 }}>
                        <ConvName>{conv.userName}</ConvName>
                        <ConvTime>{conv.startTime ? new Date(conv.startTime).toLocaleDateString('pt-BR') : '—'}</ConvTime>
                      </Flex>
                      <ConvMeta>
                        <span>{conv.userId}</span>
                        <span>·</span>
                        <span>{conv.agentId ? agentName(conv.agentId) : '—'}</span>
                        <span>·</span>
                        <span>{conv.messageCount} {t('msgs')}</span>
                      </ConvMeta>
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Badge variant={conv.status === 'completed' ? 'success' : conv.status === 'expired' ? 'neutral' : conv.status === 'queued' ? 'info' : conv.status === 'open' ? 'warning' : 'warning'}>
                          {conv.status === 'completed' ? t('statusCompleted') : conv.status === 'expired' ? t('statusExpired') : conv.status === 'queued' ? t('statusQueued') : conv.status === 'open' ? t('statusOpen', { defaultValue: 'Em andamento' }) : t('statusActive')}
                        </Badge>
                        {conv.migratedToHuman && (
                          <Badge variant="info" title={conv.humanAgentId || ''}>
                            {t('transferredToHuman', { defaultValue: '👤 Transferida p/ humano' })}
                          </Badge>
                        )}
                      </div>
                    </ConvItem>
                  ))}
                </ListScroll>
              )}
              {nextKey && (
                <LoadMoreBtn onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? <Spinner size="13px" /> : t('common:loadMore')}
                </LoadMoreBtn>
              )}
            </Card>

            {/* Detalhe */}
            <Card style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {selected ? (
                <>
                  <DetailHeader>
                    <Flex justify="space-between" align="center">
                      <div>
                        <DetailName>{selected.userName}</DetailName>
                        <DetailMeta>
                          {selected.userId} · {agentName(selected.agentId)} · {selected.connectorType}
                          {selected.startTime && ` · ${new Date(selected.startTime).toLocaleString('pt-BR')}`}
                        </DetailMeta>
                        {/* Metadados de atendimento / transferência (Requirement 6) */}
                        <DetailBadges>
                          <HandoffBadge>
                            {selected.handledBy === 'human'
                              ? t('handledByHuman', { defaultValue: '👤 Atendido por humano' })
                              : t('handledByAi', { defaultValue: '🤖 Atendido por IA' })}
                          </HandoffBadge>
                          {selected.migratedToHuman && (
                            <HandoffBadge>{t('migratedToHuman', { defaultValue: 'Transferido p/ humano' })}</HandoffBadge>
                          )}
                          {selected.handoffCategory && (
                            <HandoffBadge title={selected.handoffReason || ''}>
                              {t('handoffCategory', { defaultValue: 'Motivo' })}: {selected.handoffCategory}
                            </HandoffBadge>
                          )}
                          {selected.outcome && (
                            <HandoffBadge>{t('outcome', { defaultValue: 'Desfecho' })}: {selected.outcome}</HandoffBadge>
                          )}
                        </DetailBadges>
                        {selected.handoffReason && (
                          <DetailReason>"{selected.handoffReason}"</DetailReason>
                        )}
                      </div>
                      {selected.status === 'active' && (
                        <Flex style={{ gap: theme.spacing[2] }}>
                          <QueueBtn onClick={() => handleSendToQueue(selected)} disabled={queuing}>
                            <UserPlus size={13} />
                            {t('sendToQueue')}
                          </QueueBtn>
                          <TerminateBtn onClick={() => handleTerminate(selected)} disabled={terminating}>
                            <XCircle size={13} />
                            {t('terminateSession')}
                          </TerminateBtn>
                        </Flex>
                      )}
                    </Flex>
                  </DetailHeader>
                  <MessageScroll>
                    {transcriptLoading ? (
                      <LoadingContainer style={{ padding: theme.spacing[8] }}>
                        <Spinner />
                      </LoadingContainer>
                    ) : transcriptExpired ? (
                      <EmptyState style={{ padding: theme.spacing[8] }}>
                        <EmptyIcon>🗄️</EmptyIcon>
                        <EmptyText>{t('transcriptExpired', { defaultValue: 'Transcrição indisponível' })}</EmptyText>
                        <EmptyHint style={{ marginTop: theme.spacing[3], textAlign: 'center' }}>
                          {t('transcriptExpiredHint', { defaultValue: 'A transcrição desta conversa não está mais disponível (fora do período de retenção ou sessão anterior ao registro).' })}
                        </EmptyHint>
                      </EmptyState>
                    ) : selected.messages.length === 0 ? (
                      <EmptyState style={{ padding: theme.spacing[8] }}>
                        <EmptyText>{t('noMessages')}</EmptyText>
                      </EmptyState>
                    ) : (
                      selected.messages.map((msg, i) => (
                        <MsgRow key={i} $user={msg.role === 'user'}>
                          <MsgBubble $user={msg.role === 'user'}>
                            <MsgMeta>
                              {msg.role === 'user'
                                ? selected.userName
                                : (selected.handledBy === 'human'
                                    ? (selected.humanAgentId || t('handledByHumanShort', { defaultValue: 'Atendente' }))
                                    : agentName(selected.agentId))}
                              {msg.timestamp && ` · ${new Date(msg.timestamp).toLocaleString('pt-BR')}`}
                            </MsgMeta>
                            <MsgText>{formatContent(msg.content)}</MsgText>
                          </MsgBubble>
                        </MsgRow>
                      ))
                    )}
                  </MessageScroll>
                </>
              ) : (
                <EmptyState style={{ flex: 1 }}>
                  <EmptyIcon>💬</EmptyIcon>
                  <EmptyText>{t('selectConversation')}</EmptyText>
                </EmptyState>
              )}
            </Card>
          </Grid>
          </>
        )}
      </PageContainer>
    </>
  );
}

const FilterBar = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;
const FilterInput = styled.input`
  flex: 1; min-width: 200px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const FilterSelect = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; background: white; outline: none; cursor: pointer;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const FilterCount = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; white-space: nowrap;`;

const FilterDateGroup = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
`;
const FilterDateLabel = styled.span`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; white-space: nowrap;
`;
const FilterDate = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; background: white; outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const ClearDatesBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  padding: ${theme.spacing[1]}; background: transparent; border: none;
  color: ${theme.colors.textMuted}; cursor: pointer;
  &:hover { color: ${theme.colors.primary}; }
`;

const DetailBadges = styled.div`
  display: flex; flex-wrap: wrap; gap: ${theme.spacing[1]}; margin-top: ${theme.spacing[2]};
`;
const HandoffBadge = styled.span`
  font-size: 10px; padding: 2px 8px; border-radius: ${theme.radii.md};
  background: rgba(255,255,255,0.15); color: white; white-space: nowrap;
`;
const DetailReason = styled.div`
  font-size: ${theme.typography.xs}; opacity: 0.8; margin-top: ${theme.spacing[2]}; font-style: italic;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: ${theme.spacing[4]};
  height: calc(100vh - 220px);
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const ListHeader = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary}; color: white;
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
`;

const ListScroll = styled.div`overflow-y: auto; height: calc(100% - 44px);`;
const LoadMoreBtn = styled.button`width: 100%; padding: ${theme.spacing[3]}; background: ${theme.colors.gray50}; border: none; border-top: 1px solid ${theme.colors.border}; font-size: ${theme.typography.sm}; color: ${theme.colors.primary}; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]}; &:hover { background: ${theme.colors.primaryLight}; } &:disabled { opacity: 0.5; cursor: not-allowed; }`;

const ConvItem = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  background: ${p => p.$active ? theme.colors.primaryLight : theme.colors.white};
  &:hover { background: ${theme.colors.gray50}; }
`;

const ConvName = styled.div`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ConvTime = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;

const ConvMeta = styled.div`
  display: flex; gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
`;

const DetailHeader = styled.div`
  padding: ${theme.spacing[4]};
  background: #1e293b; color: white; flex-shrink: 0;
`;

const DetailName = styled.div`
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  margin-bottom: 4px;
`;

const DetailMeta = styled.div`font-size: ${theme.typography.xs}; opacity: 0.7;`;

const MessageScroll = styled.div`flex: 1; overflow-y: auto; padding: ${theme.spacing[4]};`;

const MsgRow = styled.div`
  display: flex;
  justify-content: ${p => p.$user ? 'flex-end' : 'flex-start'};
  margin-bottom: ${theme.spacing[3]};
`;

const MsgBubble = styled.div`
  max-width: 72%;
  background: ${p => p.$user ? theme.colors.primary : theme.colors.gray100};
  color: ${p => p.$user ? 'white' : theme.colors.textPrimary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
`;

const MsgMeta = styled.div`
  font-size: 10px; opacity: 0.7; margin-bottom: 4px;
`;

const MsgText = styled.div`
  font-size: ${theme.typography.sm}; line-height: 1.5; white-space: pre-wrap;
`;

const TerminateBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: transparent; color: #fca5a5;
  border: 1px solid #fca5a5; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; white-space: nowrap;
  &:hover:not(:disabled) { background: rgba(252,165,165,0.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const QueueBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: transparent; color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; white-space: nowrap;
  &:hover:not(:disabled) { background: ${theme.colors.primaryLight}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ExportBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${p => p.$secondary ? theme.colors.gray100 : theme.colors.primary};
  color: ${p => p.$secondary ? theme.colors.textSecondary : 'white'};
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.9; }
`;

const SkeletonList = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  overflow: hidden;
  & > * + * { border-top: 1px solid ${theme.colors.gray50}; }
`;
